/**
 * Server-authoritative operations, addressed by name with typed input/output.
 *
 * Anything that must not be decided by a client lives here: approvals,
 * releases, sequential numbering, rollups, notifications. The catalogue is a
 * plain interface so each adapter maps it to its own transport, and so tests
 * can register a handler without a network.
 */
export interface FunctionCatalog {
  ping: { input: { message: string }; output: { echo: string; at: string } };

  /**
   * Create a company, its built-in roles, and the caller's administrator
   * membership — atomically, on the server.
   *
   * Done here rather than from the client because it spans four documents
   * that must all exist or none. Doing it client-side would mean rules had
   * to permit a user to write their own membership, which is the exact hole
   * an attacker would use to grant themselves administrator in someone
   * else's company.
   */
  bootstrapCompany: {
    /**
     * `companyId` is generated by the caller, per AGENTS.md section 3, and
     * is what makes the operation safe to retry: the same id is the same
     * company, so a double-clicked button or a retried call converges on one
     * record instead of creating two. A server-generated id cannot do that —
     * every attempt would be a different company.
     */
    input: {
      companyId: string;
      companyName: string;
      timeZone?: string;
      currency?: string;
      /**
       * Whether to write the NAHB-style starter chart of accounts (P5-02).
       *
       * Defaults to true server-side. A company with no chart cannot
       * estimate, and "where do I start" is a worse first experience than
       * "delete what you do not use" — but a company that already has its
       * own chart says false and imports theirs instead.
       */
      starterCostCodes?: boolean;
    };
    output: { companyId: string };
  };

  /**
   * Invite someone into a company. Returns the token so the caller can share
   * the link; there is no email provider until Phase 3, and silently doing
   * nothing would be worse than being explicit about it.
   */
  inviteMember: {
    input: {
      companyId: string;
      email: string;
      roleId: string;
      kind: 'internal' | 'sub' | 'client';
      jobIds?: string[];
      /**
       * The directory record this invite is for (SCRUM-315).
       *
       * When it is given, accepting the invite links the new membership
       * to that contact — which is what lets a sub who priced a package
       * by email see that price as theirs once they sign in. Without it
       * the server matches on the address instead, and that is a guess
       * the moment two contacts share one.
       */
      contactId?: string;
    };
    /**
     * `emailed` says whether the invitation was put in the mail queue.
     *
     * Reported rather than assumed, so the screen can say "sent to dana@…"
     * when it was and keep offering the link when it was not — a local run
     * and an unknown project both deliver nothing, and a page claiming an
     * email had gone out is how somebody waits a day for a message that
     * was never coming.
     */
    output: { token: string; inviteId: string; emailed: boolean };
  };

  /** Redeem an invite for the signed-in user. */
  acceptInvite: {
    input: { token: string };
    /**
     * `contactId` comes back when the new membership was linked to a
     * directory record (SCRUM-315), so a screen can say which party
     * somebody has been recognised as rather than leaving it implied.
     */
    output: { companyId: string; companyName: string; contactId?: string };
  };

  /**
   * What a trade is being asked to price, from an emailed link
   * (SCRUM-308).
   *
   * Deliberately narrow: one package, and their own earlier price if
   * they sent one. A bearer token is whoever holds it, so what it can
   * read is drawn as tightly as what it can write — no other packages,
   * no other trades, and nobody else's amount.
   */
  bidPackageForToken: {
    input: { token: string };
    output: {
      title: string;
      scope: string;
      dueDate?: string;
      companyName: string;
      open: boolean;
      myAmountMinor: number | null;
      myNotes: string;
    };
  };

  /** Record this trade's price, without an account (SCRUM-308). */
  priceBidByToken: {
    input: { token: string; amountMinor: number; notes?: string };
    output: { priced: boolean };
  };

  /**
   * Send a trade their pricing link again (SCRUM-314).
   *
   * Mints a new token and **retires the one they had**: two live links
   * for one trade would mean a price arriving through one somebody
   * believed they had replaced.
   *
   * `sendId` is minted by the page when the button is pressed, so a
   * retried request is the same send and the next deliberate press is a
   * different one. `attempt` comes back so the screen can say how many
   * times this trade has been written to rather than only that
   * something happened.
   */
  resendBidLink: {
    input: {
      companyId: string;
      jobId: string;
      packageId: string;
      contactId: string;
      sendId: string;
    };
    output: { to: string; expiresAt: string; emailed: boolean; attempt: number };
  };

  /**
   * Create a change order and allocate its number in one transaction.
   *
   * The number is the reason this is a callable rather than a client write.
   * `0007` is sequential per job, and a sequence cannot be client-generated
   * without two change orders claiming the same one — the counter has to be
   * read, incremented and written where nothing can interleave.
   *
   * `changeOrderId` is generated by the caller, per AGENTS.md section 3, and
   * is what makes the call safe to retry: the transaction finds the document
   * already there and returns the number it already has, rather than burning
   * a second number on the same intent. A server-generated id would make
   * every attempt a new change order with a new number, and the gaps in the
   * sequence would be the only evidence.
   */
  createChangeOrder: {
    input: {
      companyId: string;
      jobId: string;
      changeOrderId: string;
      title: string;
      description?: string;
      approvalDeadline?: string;
    };
    output: { changeOrderId: string; number: string };
  };

  /**
   * Move a change order from one state to the next.
   *
   * Server-side because the transitions are the rules: an owner may approve
   * a released change order and nothing else, releasing is what shows a
   * price to a customer, and an approval has to record who, when and what
   * they agreed to. A client that could write `status` could approve its own
   * change order.
   */
  transitionChangeOrder: {
    input: {
      companyId: string;
      jobId: string;
      changeOrderId: string;
      to: 'released' | 'approved' | 'declined' | 'draft' | 'void';
      reason?: string;
      signaturePath?: string;
      comment?: string;
      disclaimer?: string;
      /** The office acting for an owner who answered by telephone. */
      onBehalfOf?: string;
    };
    output: { status: string };
  };

  /**
   * Give a draft change order a number somebody typed.
   *
   * A callable for the same reason the create is, and for one more. The
   * **uniqueness** check cannot live in a security rule: a rule can see the
   * document being written and ask whether `number` changed, but it cannot
   * ask whether another change order in the job already holds the new
   * number. And the **counter** has to be pushed past a number typed by
   * hand, or it will hand that number out again later — a collision set up
   * weeks in advance, which is the worst kind.
   *
   * Idempotent on the number itself: renumbering to the number it already
   * has writes nothing and succeeds, because a double-clicked Save is one
   * act.
   */
  renumberChangeOrder: {
    input: { companyId: string; jobId: string; changeOrderId: string; number: string };
    output: { number: string };
  };

  /**
   * A short-lived link to one file's bytes.
   *
   * Server-side because **storage rules cannot read Firestore**. They can
   * ask whether somebody is signed in; they cannot ask whether this file is
   * shared with subs, whether the caller is on this job, or whether it has
   * passed its checks. The bucket therefore refuses every client read, and
   * this is the only way to the bytes — where those questions can be asked
   * with the same `isVisibleTo` the UI mirrors and the rules enforce.
   */
  fileDownloadUrl: {
    input: { companyId: string; fileId: string };
    output: { url: string; expiresAt: string };
  };

  /**
   * Record a file an owner or a subcontractor uploaded (P3-04b).
   *
   * An external member holds `files: view`, so the rules refuse them the
   * document write — and three of its fields are not theirs to choose.
   * **Where it goes** follows from who is asking, because the feature
   * doc puts an owner's upload in "From Owner" and a sub's in "From
   * Subs"; **how big it is and what it is** are read back from the
   * stored object, because a caller that states its own size can lie to
   * a quota; and **who may see it** is the company plus the side that
   * sent it, never the other side.
   *
   * The bytes go up first, through the storage rules, which can ask only
   * whether somebody is signed in. That is exactly why the document
   * cannot be written the same way.
   *
   * Idempotent on `fileId`, which the client generates: a dropped
   * response and a double-tapped button are the same upload.
   */
  recordExternalUpload: {
    input: {
      companyId: string;
      jobId: string;
      fileId: string;
      name: string;
      contentType: string;
      /**
       * What to attach it to, if anything (P8-12a2). Both or neither —
       * half a reference is refused rather than treated as none.
       *
       * The caller's right to **read** that record is checked before
       * anything is written, by the same function `recordAttachments`
       * uses: a sub who could attach a file to a claim they cannot see
       * would have put it in front of everybody who can.
       */
      recordKind?: 'dailyLog' | 'todo' | 'scheduleItem' | 'job' | 'warrantyClaim' | 'specSection';
      recordId?: string;
    };
    output: { fileId: string; folderId: string | null; duplicate: boolean };
  };

  /**
   * What is attached to one record, for a caller who may not list files.
   *
   * Server-side because **listing `files` is internal-only and must stay
   * that way**: the document carries `storagePath`, and `jobId` is a field
   * rather than a path segment, so a per-document `list` rule cannot ask
   * whether the caller is on the job without one read per returned file.
   *
   * The function checks the *record* first — you may enumerate the
   * attachments of something you can read and of nothing else — then the
   * job, then each file's audience, and returns a projection with no
   * `storagePath` in it. `docs/features/11-files-messaging.md` §7 has the
   * three designs that were rejected and why.
   */
  recordAttachments: {
    input: {
      companyId: string;
      /**
       * Spelled out rather than imported, because a port may not depend
       * on a use case. `record-attachments.ts` asserts at compile time
       * that this union and `ATTACHABLE_KINDS` agree, so the duplication
       * cannot drift silently.
       */
      recordKind: 'dailyLog' | 'todo' | 'scheduleItem' | 'job' | 'warrantyClaim' | 'specSection';
      recordId: string;
      jobId?: string;
    };
    output: {
      attachments: {
        id: string;
        name: string;
        size: number;
        contentType: string;
        createdAt: string;
      }[];
    };
  };

  /**
   * A change order as a PDF, rendered for whoever is asking.
   *
   * Server-side because **which document you get depends on who you are**:
   * an owner gets the mirror, which never carried a cost, and internal
   * staff get the real one with cost only if their role carries
   * `seeBuilderCost`. A browser-side renderer would have to be trusted to
   * leave the cost out of a document it was already holding.
   *
   * Base64 rather than a signed URL: it is a few kilobytes, it is derived
   * rather than stored, and a URL would be one more thing that expires.
   */
  changeOrderPdf: {
    /**
     * `copy` chooses the document and can only ever take cost away —
     * the same rule as `invoicePdf`, and for the same reason. An
     * invoice and a change order reach the same customer in the same
     * week; a builder's copy of one and a customer's copy of the other
     * would be a difference nobody could explain.
     */
    input: {
      companyId: string;
      jobId: string;
      changeOrderId: string;
      copy?: 'customer' | 'builder';
    };
    output: { filename: string; base64: string };
  };

  /**
   * Render one invoice as a PDF, server-side (P5-19).
   *
   * The same shape as `changeOrderPdf` and the same reasoning: the server
   * decides which document the caller is allowed to see, because an owner
   * gets the mirror — which never carried a cost — and internal staff get
   * the real one with cost only if their role carries `seeBuilderCost`. A
   * browser-side renderer would have to be trusted to leave the cost out
   * of a document it was already holding, and doc 09 §6 asks for the
   * opposite of trust: "Owner cannot see Builder Cost or Markup in the
   * invoice view **or PDF**."
   *
   * Base64 rather than a signed URL, for the same three reasons: it is a
   * few kilobytes, it is derived rather than stored, and a URL would be
   * one more thing that expires.
   */
  invoicePdf: {
    /**
     * `copy` chooses the document, and the choice can only ever take
     * cost away (SCRUM-272): `builder` means "cost if your role carries
     * `seeBuilderCost`", never "cost because I asked". Absent means
     * `customer`, because an administrator printing an invoice is
     * usually printing it to send to their customer — which is how a
     * PDF carrying the builder's margin reached one.
     */
    input: {
      companyId: string;
      jobId: string;
      invoiceId: string;
      copy?: 'customer' | 'builder';
    };
    output: { filename: string; base64: string };
  };

  /**
   * Turn an approved estimate into the job's original budget and contract.
   *
   * Server-side because approving does **three** things across other
   * collections: it marks the estimate approved, writes a budget row per
   * cost code, and supersedes whichever estimate was approved before. A
   * client that could write those could approve its own estimate, and a
   * client that managed two of the three would leave a job with two live
   * contracts or a budget belonging to nothing.
   *
   * Idempotent: approving an already-approved estimate reports
   * `alreadyApproved` rather than writing the budget a second time.
   */
  approveEstimate: {
    input: { companyId: string; jobId: string; estimateId: string };
    output: { estimateId: string; budgetRows: number; alreadyApproved: boolean };
  };

  /**
   * Raise a purchase order and allocate its number (P5-09a).
   *
   * Server-side because the number is sequential per job and is read out
   * to a supplier: two buyers raising one in the same minute would
   * otherwise both be `SFM-0007`, and a supplier would hold two documents
   * with one number. `purchaseOrderId` is the caller's, so a retry finds
   * the document already there and returns the number it already has
   * rather than burning a second one.
   */
  /**
   * The next stock number for the company (INV-03).
   *
   * Server-side for the reason a purchase order number is: it is read
   * out loud and printed on a label, so two items must never carry the
   * same one. **Idempotent on the item** — one that already has a SKU
   * gets the one it has, so a double-clicked button cannot renumber a
   * shelf label somebody has already written out.
   */
  allocateSku: {
    input: { companyId: string; catalogItemId: string };
    output: { catalogItemId: string; sku: string };
  };

  createPurchaseOrder: {
    input: {
      companyId: string;
      jobId: string;
      purchaseOrderId: string;
      title: string;
      assignedContactId?: string;
      scope?: string;
      materialsOnly?: boolean;
      /**
       * The awarded bid package this order comes out of (P8-10d). Only
       * the id travels — the server reads the package for the provenance
       * it writes, so an order cannot claim bids it was never part of.
       */
      sourceBidPackageId?: string;
    };
    output: { purchaseOrderId: string; number: string };
  };

  /**
   * Move a purchase order between states.
   *
   * Releasing commits the company's money, and the budget's `committed`
   * column moves with it — so a client that could write `status` could
   * order work nobody approved. Unreleasing and voiding need `admin`,
   * because both take back a document a supplier is already working to.
   *
   * Idempotent: asking for the state it is already in reports that state
   * rather than failing, because a double-clicked Release is one act.
   */
  transitionPurchaseOrder: {
    input: {
      companyId: string;
      jobId: string;
      purchaseOrderId: string;
      to: 'released' | 'approved' | 'unreleased' | 'void';
      reason?: string;
    };
    output: { purchaseOrderId: string; status: string };
  };

  /**
   * Mark the work on a purchase order complete, or not.
   *
   * A callable because the **sub** does it from their own portal, and a
   * sub may not write a purchase order document at all. This is the one
   * change they are allowed, narrowed to two fields and to their own PO —
   * matched on their membership's contact id, never on anything in the
   * request.
   */
  setPurchaseOrderWorkComplete: {
    input: { companyId: string; jobId: string; purchaseOrderId: string; complete: boolean };
    output: { purchaseOrderId: string; complete: boolean };
  };

  /**
   * Raise a bill and allocate its number (P5-10a).
   *
   * Server-side for the same reason purchase orders are: the number is
   * sequential per job and it is what a bookkeeper files the invoice
   * under. `billId` is the caller's, so a retry returns the number it
   * already has rather than leaving two documents to reconcile.
   */
  createBill: {
    input: {
      companyId: string;
      jobId: string;
      billId: string;
      title: string;
      payToContactId?: string;
      purchaseOrderId?: string;
    };
    output: { billId: string; number: string };
  };

  /**
   * A sub's own invoice against a purchase order they were given (P5-10g).
   *
   * Separate from `createBill` because almost every decision differs: the
   * payee is the caller, read from their membership rather than chosen;
   * the purchase order is required; the lines are **derived** by
   * apportioning the amount across the order's own lines, so a sub is
   * never asked to pick a cost code out of somebody else's chart of
   * accounts; and the amount is capped at what is left on the order. The
   * bill arrives `open`, so the office reviews it before any money moves.
   */
  submitSubBill: {
    input: {
      companyId: string;
      jobId: string;
      purchaseOrderId: string;
      billId: string;
      title: string;
      amountMinor: number;
      invoiceDate?: string;
      description?: string;
    };
    output: { billId: string; number: string };
  };

  /**
   * Acknowledge a safety notice (SCRUM-208).
   *
   * A callable for one reason, and it is the only one that matters: the
   * **revision is stamped by the server**. A client that could choose it
   * would acknowledge revision 1 of a notice now on revision 3 and read as
   * compliant, which is precisely what this record must never permit. The
   * uid comes from the token for the same reason.
   *
   * Idempotent — the document id is derived from the uid and the revision,
   * so a double press is one acknowledgement keeping its first timestamp.
   */
  /**
   * Relay one turn of an assistant conversation (SCRUM-223).
   *
   * **It reads none of the company's data.** It holds the API key,
   * assembles the prompt, calls the model, meters the result, and returns
   * either prose or a request to run a tool. The browser runs the tool,
   * through the ordinary ports, in the person's own session — so
   * Firestore refuses the assistant on exactly the documents it refuses
   * them, and there is no second authorisation path to keep in step.
   *
   * Refuses with `failed-precondition` for a company with no allowance
   * (off, never unlimited) and `resource-exhausted` once the month's cap
   * is reached.
   */
  askAssistant: {
    input: {
      companyId: string;
      messages: unknown[];
      model: string;
      step?: number;
    };
    output: {
      text: string;
      toolUses: { id: string; name: string; input: unknown }[];
      stopReason: string;
      provider: 'anthropic' | 'stub';
      usage: {
        inputTokens: number;
        outputTokens: number;
        cacheReadTokens: number;
        cacheWriteTokens: number;
      };
      remaining: number;
      nearlyGone: boolean;
      stepsLeft: number;
    };
  };

  acknowledgeSafetyNotice: {
    input: { companyId: string; noticeId: string };
    output: { revision: number; at: string };
  };

  /**
   * Chase the people who have not acknowledged a notice (P5-23).
   *
   * Needs `settings: edit`: it sends mail to other people, and who has
   * and has not complied is not everybody's business.
   *
   * **The chase carries no in-app channel**, and that is the design
   * rather than an omission. The splash is a live subscription nobody can
   * dismiss without acknowledging, so anybody still outstanding has not
   * had the app open since the notice went up — the one audience an
   * in-app message cannot reach. The email lands them on `/app`, where
   * the splash is waiting.
   *
   * Idempotent per day: ids are derived from the notice, the revision,
   * the recipient and the company's current date, so a double press is
   * one chase while a deliberate chase tomorrow is a new one.
   */
  chaseSafetyNotice: {
    input: { companyId: string; noticeId: string };
    output: { chased: number; revision: number };
  };

  /**
   * Move a bill between `open`, `readyForPayment` and `void`.
   *
   * `readyForPayment` needs the **`markReadyForPayment`** flag: it is the
   * decision that releases money, and "who said this could be paid" has to
   * be answerable afterwards. Voiding is refused outright once a payment
   * has been recorded — a voided bill counts towards nothing, so voiding a
   * paid one would take money out of `actual` that has left the bank.
   */
  transitionBill: {
    input: {
      companyId: string;
      jobId: string;
      billId: string;
      to: 'open' | 'readyForPayment' | 'void';
    };
    output: { billId: string; status: string };
  };

  /**
   * Record a payment that happened outside this system.
   *
   * A callable, and the one in this product closest to touching money: it
   * needs the **`recordPayments`** flag, it refuses a non-positive amount,
   * and it is idempotent on `paymentId` so a double-clicked button is one
   * payment. `amountMinor` is integer minor units — the port carries no
   * `Money` because the callable boundary is JSON and a currency mismatch
   * is better caught by the server reading the job's own currency.
   */
  recordBillPayment: {
    input: {
      companyId: string;
      jobId: string;
      billId: string;
      paymentId: string;
      amountMinor: number;
      method: 'check' | 'card' | 'ach' | 'cash' | 'other';
      reference?: string;
    };
    output: { paymentId: string; paidMinor: number };
  };

  /**
   * Turn a receipt in the cost inbox into a bill (P5-11).
   *
   * A callable because it writes a bill with an allocated number in
   * another collection and marks the receipt converted in the same breath.
   * A client that managed half of it would leave a receipt pointing at a
   * bill that does not exist — or a bill nothing accounts for.
   *
   * Idempotent twice over: on the caller's `billId`, and on the receipt's
   * own state, so a receipt already converted reports
   * `alreadyConverted` with the bill it made rather than making a second.
   */
  convertReceiptToBill: {
    input: { companyId: string; jobId: string; receiptId: string; billId: string };
    output: { billId: string; number: string; alreadyConverted: boolean };
  };

  /**
   * Raise an owner invoice and allocate its number (P5-12a).
   *
   * Server-side like every other numbered document here: the sequence is
   * per job and the number is what an owner quotes back at you.
   */
  createInvoice: {
    input: { companyId: string; jobId: string; invoiceId: string; title: string };
    output: { invoiceId: string; number: string };
  };

  /**
   * Release an owner invoice, pull it back, or void it.
   *
   * **Releasing is the moment the owner is asked for money**, which is why
   * a client may not write `status`. Pulling one back is refused once a
   * payment exists — the owner has acted on the document, so changing it
   * would be rewriting something they paid against, and a credit memo is
   * the instrument for that. Voiding is refused on the same grounds.
   */
  transitionInvoice: {
    input: {
      companyId: string;
      jobId: string;
      invoiceId: string;
      to: 'released' | 'unreleased' | 'void';
    };
    output: { invoiceId: string; status: string };
  };

  /**
   * Record a payment against an owner invoice.
   *
   * Needs `recordPayments`, and is idempotent on the caller's `paymentId`
   * for the reason a bill's is: a retried write with a server-generated id
   * would be a second payment for the same cheque, and here the cheque is
   * the customer's.
   */
  recordInvoicePayment: {
    input: {
      companyId: string;
      jobId: string;
      invoiceId: string;
      paymentId: string;
      amountMinor: number;
      method: 'check' | 'card' | 'ach' | 'cash' | 'other';
      reference?: string;
    };
    output: { paymentId: string; paidMinor: number };
  };

  /**
   * Record that the owner has opened an invoice (P5-12c).
   *
   * A callable because it writes the **office's** document: the owner
   * reads a mirror, and the office is the one who wants to know whether
   * their customer has looked. Idempotent — the first view is the
   * interesting fact, and re-stamping would turn "seen on the 3rd" into
   * "seen just now".
   */
  markInvoiceViewed: {
    input: { companyId: string; jobId: string; invoiceId: string };
    output: { viewedAt: string };
  };

  /**
   * Credit memos and deposits (P5-13).
   *
   * All five are callables because each **changes what a customer owes**,
   * and three refusals elsewhere in this phase point at them: unreleasing
   * a paid invoice, voiding one, and voiding a paid bill all say "raise a
   * credit memo instead". Each needs `recordPayments`, which
   * `docs/PERMISSIONS.md` §3 describes as "can record offline payments /
   * deposits" — deposits by name. Voiding a credit needs `invoices: admin`
   * as well, because it takes money back off a customer.
   */
  createCreditMemo: {
    input: {
      companyId: string;
      jobId: string;
      creditMemoId: string;
      amountMinor: number;
      reason: string;
    };
    output: { creditMemoId: string; number: string };
  };

  /** Put a held credit against a **released** invoice, in one transaction. */
  applyCreditMemo: {
    input: { companyId: string; jobId: string; creditMemoId: string; invoiceId: string };
    output: { creditMemoId: string; invoiceId: string };
  };

  voidCreditMemo: {
    input: { companyId: string; jobId: string; creditMemoId: string };
    output: { creditMemoId: string };
  };

  recordDeposit: {
    input: {
      companyId: string;
      jobId: string;
      depositId: string;
      amountMinor: number;
      receivedAt: string;
      method: 'check' | 'card' | 'ach' | 'cash' | 'other';
      reference?: string;
    };
    output: { depositId: string; number: string };
  };

  /**
   * Put part of a deposit against an invoice.
   *
   * Refuses more than is left, computed inside the transaction: a deposit
   * applied twice over would be money the builder never received, reaching
   * the owner as a balance that looked settled.
   */
  applyDeposit: {
    input: {
      companyId: string;
      jobId: string;
      depositId: string;
      invoiceId: string;
      amountMinor: number;
    };
    output: { depositId: string; invoiceId: string; remainingMinor: number };
  };

  /** Revoke an invite that has not been accepted. */
  revokeInvite: {
    input: { companyId: string; inviteId: string };
    output: { revoked: boolean };
  };
}

export type FunctionName = keyof FunctionCatalog;

export interface FunctionsPort {
  call<N extends FunctionName>(
    name: N,
    input: FunctionCatalog[N]['input'],
  ): Promise<FunctionCatalog[N]['output']>;
}

export class FunctionCallError extends Error {
  readonly functionName: string;
  readonly code: string;

  constructor(functionName: string, message: string, code = 'internal') {
    super(`${functionName}: ${message}`);
    this.name = 'FunctionCallError';
    this.functionName = functionName;
    this.code = code;
  }
}
